import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ContractComment, JsonContractComment } from '@core/models/contract-comment/contract-comment.model';
import { BaseService } from '@core/services/base/base.service';
import { ErrorHandlerService, HttpError } from '@core/services/error-handler.service';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

type ApiResponse<T> = { data?: T };

@Injectable({
  providedIn: 'root',
})
export class ContractCommentService extends BaseService {
  private httpErrors: HttpError[] = [
    { statusCode: 404, message: 'Commentaire non trouvé.' },
    { statusCode: 500, message: 'Erreur serveur, veuillez réessayer plus tard.' },
  ];

  constructor(private httpService: HttpClient, private errorHandlerService: ErrorHandlerService) {
    super();
  }

  public getCommentsFor1Contract(contractId: string): Observable<ContractComment[]> {
    if (!contractId) return;

    const url = `${environment.contract_microservice_api_base_url}/contract_comment?contractId=${contractId}`;

    return this.httpService.get<ApiResponse<JsonContractComment[]>>(url).pipe(
      map(response => {
        const data = response?.data;
        if (!Array.isArray(data)) return [];

        const comments = data.map((comment: JsonContractComment) => new ContractComment(comment));

        comments.sort((a, b) => {
          const ta = a.commentDate ? new Date(a.commentDate).getTime() : 0;
          const tb = b.commentDate ? new Date(b.commentDate).getTime() : 0;
          if (ta !== tb) return ta - tb;

          return Number(a.id) - Number(b.id);
        });

        return comments;
      }),
      catchError(error => {
        this.errorHandlerService.showErrorMessage(this.httpErrors, error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  public deleteComment(commentId: string): Observable<void> {
    const url = `${environment.contract_microservice_api_base_url}/contract_comment/${commentId}`;

    return this.httpService.delete<void>(url).pipe(
      catchError(error => {
        this.errorHandlerService.showErrorMessage(this.httpErrors, error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  public updateComment(comment: ContractComment): Observable<ContractComment> {
    const url = `${environment.contract_microservice_api_base_url}/contract_comment/${comment.id}`;

    return this.httpService.put<ApiResponse<JsonContractComment>>(url, comment as any).pipe(
      map(res => new ContractComment(res?.data ?? null)),
      catchError(error => {
        this.errorHandlerService.showErrorMessage(this.httpErrors, error.status, error.message);
        return throwError(() => error);
      })
    );
  }

  public addComment(comment: ContractComment): Observable<ContractComment> {
    const url = `${environment.contract_microservice_api_base_url}/contract_comment`;

    return this.httpService.post<ApiResponse<JsonContractComment>>(url, comment as any).pipe(
      map(res => new ContractComment(res?.data ?? null)),
      catchError(error => {
        this.errorHandlerService.showErrorMessage(this.httpErrors, error.status, error.message);
        return throwError(() => error);
      })
    );
  }
}
